/**
 * MyMouvScanner — Organizer ticket scanner (QR + manual number).
 * Anti-fraud color code: green=valid, blue=already scanned, yellow=VIP, red=invalid.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ScanLine, Search, Loader2, CheckCircle2, AlertCircle, XCircle, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { BrowserQRCodeReader } from '@zxing/browser';

const COLOR_MAP = {
  green: 'bg-emerald-500/20 border-emerald-400/60 text-emerald-100',
  blue: 'bg-blue-500/20 border-blue-400/60 text-blue-100',
  vip_yellow: 'bg-yellow-400/20 border-yellow-400/60 text-yellow-100',
  yellow: 'bg-yellow-400/20 border-yellow-400/60 text-yellow-100',
  red: 'bg-red-500/20 border-red-400/60 text-red-100',
};

const ICON_MAP = {
  green: <CheckCircle2 className="w-8 h-8 text-emerald-400" />,
  blue: <AlertCircle className="w-8 h-8 text-blue-400" />,
  vip_yellow: <Crown className="w-8 h-8 text-yellow-400" />,
  yellow: <Crown className="w-8 h-8 text-yellow-400" />,
  red: <XCircle className="w-8 h-8 text-red-400" />,
};

const MyMouvScanner = () => {
  const [manualNumber, setManualNumber] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const navigate = useNavigate();

  const verify = async (payload, ticketNumber) => {
    setBusy(true);
    try {
      const r = await axios.post('/api/mymouv/scanner/verify', { payload, ticket_number: ticketNumber });
      setResult(r.data);
      // Vibrate feedback
      if (navigator.vibrate) navigator.vibrate(r.data?.color === 'red' ? [200, 100, 200] : 60);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const startCamera = async () => {
    if (scanning) return;
    setScanning(true);
    setResult(null);
    try {
      readerRef.current = new BrowserQRCodeReader();
      controlsRef.current = await readerRef.current.decodeFromVideoDevice(undefined, videoRef.current, (r, err, controls) => {
        if (r) {
          controls.stop();
          setScanning(false);
          verify(r.getText(), null);
        }
      });
    } catch (e) {
      toast.error('Impossible d\'accéder à la caméra');
      setScanning(false);
    }
  };

  const stopCamera = () => {
    try { controlsRef.current?.stop(); } catch (_e) { /* ignore */ }
    setScanning(false);
  };

  useEffect(() => () => stopCamera(), []);

  const submitManual = () => {
    if (!manualNumber.trim()) { toast.error('Entrez un numéro'); return; }
    verify(null, manualNumber.trim().toUpperCase());
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="mymouv-scanner">
      <div className="max-w-lg mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/10 hover:bg-white/20"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="text-2xl font-bold">Scanner MyMouv</h1>
        </div>

        {/* Camera area */}
        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden mb-4">
          <div className="aspect-video bg-black flex items-center justify-center relative">
            <video ref={videoRef} className={`w-full h-full object-cover ${scanning ? '' : 'hidden'}`} playsInline muted />
            {!scanning && (
              <div className="text-center text-white/60 py-8">
                <ScanLine className="w-12 h-12 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Appuyez sur « Scanner » pour activer la caméra</p>
              </div>
            )}
          </div>
          <div className="p-3 flex gap-2">
            {!scanning ? (
              <button onClick={startCamera} className="flex-1 py-2 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-600 text-sm font-semibold" data-testid="mymouv-scanner-start">Scanner un QR</button>
            ) : (
              <button onClick={stopCamera} className="flex-1 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-sm font-semibold">Arrêter</button>
            )}
          </div>
        </div>

        {/* Manual search */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4 mb-4">
          <label className="block text-xs text-white/60 mb-2">Ou entrer un numéro de billet</label>
          <div className="flex gap-2">
            <input value={manualNumber} onChange={e => setManualNumber(e.target.value.toUpperCase())} placeholder="MYMV-XXXXXXXXXX" className="flex-1 bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm font-mono" data-testid="mymouv-scanner-manual-input" />
            <button onClick={submitManual} disabled={busy} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50" data-testid="mymouv-scanner-manual-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Result */}
        {result && (
          <div className={`rounded-2xl p-5 border-2 ${COLOR_MAP[result.color] || COLOR_MAP.red}`} data-testid="mymouv-scanner-result">
            <div className="flex items-start gap-3">
              {ICON_MAP[result.color] || ICON_MAP.red}
              <div className="flex-1">
                <div className="font-bold text-lg">{result.message}</div>
                {result.ticket && (
                  <div className="mt-2 space-y-1 text-sm">
                    <div><span className="opacity-60">Événement :</span> {result.ticket.event_title}</div>
                    <div><span className="opacity-60">Type :</span> {result.ticket.ticket_type_name} {result.ticket.is_vip && '· VIP'}</div>
                    <div><span className="opacity-60">Acheteur :</span> {result.ticket.buyer_name} ({result.ticket.buyer_email})</div>
                    <div><span className="opacity-60">N° billet :</span> <span className="font-mono">{result.ticket.ticket_number}</span></div>
                    {result.ticket.scan_count > 0 && (
                      <div><span className="opacity-60">Scans :</span> {result.ticket.scan_count} (dernier: {result.ticket.last_scanned_at?.slice(0, 19).replace('T', ' ')})</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyMouvScanner;
